// CNV-003: one-click entry (Product Readiness criterion 2). Ported from ptc-statetax's STX-PROD-02
// (`services/silentSignIn.ts`), the fleet reference; only the storage keys differ.
//
// A staff member who already has a Microsoft session in this browser — they signed in to Outlook,
// Teams, or another PTC app — used to land on this app's "Sign in" interstitial and click. ssoSilent
// asks Microsoft, in a hidden iframe, whether that session exists; if it does, they are in without a
// click. (This app's MSAL cache is localStorage, so a returning person usually has an account already;
// the page this serves is the first visit, after sign-out, and after the cache is cleared.)
//
// Exactly ONE attempt per tab session, recorded BEFORE it is made: App renders a spinner while MSAL
// reports the attempt in progress and remounts the login page when it ends, so an unguarded attempt
// would retry forever. If the attempt can't be recorded, it isn't made. A failure (no Microsoft
// session, third-party cookies blocked, several sessions and no hint) just leaves today's button.
//
// ⚠ NEVER auto-`loginRedirect` on mount instead (CNV-003): a failed silent attempt would then become
// an infinite redirect loop. The iframe needs `frame-src 'self' https://login.microsoftonline.com`: it
// STARTS at the login host and ENDS on this app's own redirect URI. Deploy #1 (2026-10-21) shipped with
// only the login host and verify:deploy:browser caught the blocked last hop; `verify:deploy` checks the
// SERVED header, not this file's assumption.

import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { apiScopes } from '../config/authConfig';

/** sessionStorage: set once the attempt is made in this tab. */
export const SILENT_ATTEMPT_KEY = 'conversions.auth.silentAttempted';
/** localStorage: the username of the last person who signed in on this browser (a hint, not a credential). */
export const LOGIN_HINT_KEY = 'conversions.auth.loginHint';

/** Remembers who signed in, so the next silent attempt can name them when the browser holds several Microsoft sessions. */
export function rememberLoginHint(account: AccountInfo | null | undefined): void {
  if (!account?.username) return;
  try { localStorage.setItem(LOGIN_HINT_KEY, account.username); } catch { /* a hint is optional */ }
}

export function shouldAttemptSilentSignIn(): boolean {
  try { return sessionStorage.getItem(SILENT_ATTEMPT_KEY) === null; } catch { return false; }
}

/** Resolves true when the person is now signed in; false otherwise. Never throws. */
export async function attemptSilentSignIn(instance: IPublicClientApplication): Promise<boolean> {
  try {
    sessionStorage.setItem(SILENT_ATTEMPT_KEY, String(Date.now()));
  } catch {
    return false; // can't guarantee "once": don't try at all
  }
  let loginHint: string | null = null;
  try { loginHint = localStorage.getItem(LOGIN_HINT_KEY); } catch { /* no hint */ }

  try {
    const result = await instance.ssoSilent(loginHint ? { scopes: apiScopes, loginHint } : { scopes: apiScopes });
    if (result.account) {
      instance.setActiveAccount(result.account);
      rememberLoginHint(result.account);
    }
    return !!result.account;
  } catch {
    return false;
  }
}
